import {
  IGiftItemNotifications,
  IGiftItemProviderProps,
  IShoppingCart,
  IShoppingCartContext,
} from '@monofront/types';
import { Context, useContext, useEffect } from 'react';
import { GIFT_ITEM_QUANTITY } from '../constants';
import { IAuthState } from '../contexts';
import { getIsGiftItem } from '../utils';

/**
 * Factory function that build a useGiftItem hook
 *  This hook is used in many parts of the application to manage the gift item offered on the first order.
 * @param useAuthentication the hook that handles the authentication
 * @param useShoppingCart the hook that handles the products in the cart
 * @param GiftItemContext the context which stores the gift items
 * @param useGiftItemNotifications the hook that provides translated messages for notifications, it returns 3 functions:
 *    onRemoveGiftItem => call when the gift item is removed from cart
 *    onAddGiftItem => call when the gift item is added from cart
 *    onAddGiftItemError => call when adding a gift item generates an error
 * @returns a hook that is used in many parts of the application to manage the gift item offered on the first order.
 */
export const createUseGiftItem = (
  useAuthentication: () => Partial<IAuthState>,
  useShoppingCart: () => IShoppingCartContext,
  useShoppingCartContent: () => IShoppingCart,
  GiftItemContext: Context<Omit<IGiftItemProviderProps, 'children'>>,
  useGiftItemNotifications: () => IGiftItemNotifications,
) => {
  return () => {
    const { isAuthenticated, customer, isLoading, isMember } =
      useAuthentication();
    const { products } = useContext(GiftItemContext);
    const { onRemoveGiftItem, onAddGiftItem, onAddGiftItemError } =
      useGiftItemNotifications();

    const {
      shoppingCartId,
      addProductToShoppingCart,
      deleteProductsFromShoppingCart,
    } = useShoppingCart();
    const shoppingCart = useShoppingCartContent();

    const isEligibleToGiftItem = Boolean(
      !isLoading && (!isAuthenticated || !customer?.totalOrderPaid),
    );

    const giftItem = products?.filter(
      (product) => product.inventory_available,
    )?.[0];

    const quantityTag = giftItem?.tags?.find((tag) =>
      tag.startsWith(GIFT_ITEM_QUANTITY.STARTWITH),
    );

    const [quantity] = quantityTag?.match(/\d+/g) || [];

    const prismicGiftItem = giftItem && {
      ...giftItem,
      quantity: quantity ? Number(quantity) : 1,
    };

    const giftItemInShoppingCart = shoppingCart.products?.find((product) =>
      getIsGiftItem(product),
    );

    useEffect(() => {
      if ((isLoading && !customer) || !shoppingCartId) {
        return;
      }

      const removeGiftItem = async () => {
        removeGiftItemFromShoppingCart();
        onRemoveGiftItem(giftItem.title);
      };

      if (!isEligibleToGiftItem && shoppingCart.hasGiftItem) {
        removeGiftItem();
      }

      if (shoppingCart.hasGiftItem) {
        const { giftItems = [] } = shoppingCart;
        const giftItemsToRemove = giftItems.filter(
          (currentGiftItem) => currentGiftItem.id !== giftItem?.id,
        );

        if (giftItemsToRemove.length > 0) {
          // delete previous gift item chose by customer and not the current eligible one given
          deleteProductsFromShoppingCart(shoppingCartId, giftItemsToRemove);
        }
      }
    }, [customer?.uid, shoppingCart.giftItems]);

    const addGiftItemToShoppingCart = async () => {
      if (
        !shoppingCartId ||
        (isMember && customer?.totalOrderPaid) ||
        !giftItem ||
        giftItemInShoppingCart?.id === giftItem.id
      ) {
        return;
      }

      try {
        if (giftItemInShoppingCart?.id !== giftItem.id) {
          await removeOldGiftItemFromShoppingCart();
        }

        await addProductToShoppingCart(shoppingCartId, prismicGiftItem);

        onAddGiftItem();
      } catch (error: any) {
        onAddGiftItemError();
      }
    };

    const removeGiftItemFromShoppingCart = async () => {
      if (!shoppingCartId) {
        return;
      }

      const { giftItems = [] } = shoppingCart;

      // hotfix old gift item bug: https://gitlab.com/la-fourche/tech/shopify-theme/-/issues/38
      await deleteProductsFromShoppingCart(shoppingCartId, [
        ...giftItems,
        prismicGiftItem,
      ]);
    };

    const removeOldGiftItemFromShoppingCart = async () => {
      if (!shoppingCartId) {
        return;
      }

      const giftItemInShoppingCart = shoppingCart?.products?.filter((product) =>
        getIsGiftItem(product),
      );

      if (!giftItemInShoppingCart) {
        return;
      }

      await deleteProductsFromShoppingCart(
        shoppingCartId,
        giftItemInShoppingCart,
      );
    };

    return {
      giftItem: isEligibleToGiftItem ? giftItem : null,
      addGiftItemToShoppingCart,
      removeGiftItemFromShoppingCart,
      removeOldGiftItemFromShoppingCart,
      giftItemInShoppingCart,
    };
  };
};
